import { AuthApi } from '../auth/authApi';
import { Singleton } from '@/decorators';
import { generateQueryString } from '@/libs';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import { wrapHttpCall } from '../utils/apiResultMapper';
import { SAVE_REQUEST_TIMEOUT_MS } from '@/api/base/saveRequestTimeout';
import type {
  AddAttestationGroupRequestDTO,
  AttestationGroupResponseDTO,
  ClearAttestationGroupRequestDTO,
  ClearAttestationGroupResponseDTO,
  GetAttestationRequestDTO,
  GetAttestationResponseDTO,
  GetSummaryRequestDTO,
  GetSummaryResponseDTO,
  RemoveAttestationGroupRequestDTO,
  UpdateAttestationConditionalFieldsGroupRequestDTO,
  UpdateAttestationConditionalFieldsGroupResponseDTO,
  UpdateAttestationRequestDTO,
  UpdateAttestationResponseDTO,
  UploadFileToAttestationRequestDTO,
  DownloadFileFromAttestationRequestDTO,
  DeleteFileFromAttestationRequestDTO,
} from './DTOs';

/**
 * Attestation API (Framework Agnostic)
 * 
 * Handles attestation-related operations for application forms.
 */
@Singleton
export class AttestationApi extends AuthApi {
  constructor() {
    super();
  }

  async getAttestation(body: GetAttestationRequestDTO): Promise<Result<GetAttestationResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetAttestationResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation`,
    ));
  }

  async updateAttestation(body: UpdateAttestationRequestDTO): Promise<Result<UpdateAttestationResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<UpdateAttestationResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation`,
      body.attestationField,
      { timeout: SAVE_REQUEST_TIMEOUT_MS },
    ));
  }

  async getSummary(body: GetSummaryRequestDTO): Promise<Result<GetSummaryResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetSummaryResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation/summary`,
    ));
  }

  async updateAttestationConditionalFieldsGroup(
    body: UpdateAttestationConditionalFieldsGroupRequestDTO,
  ): Promise<Result<UpdateAttestationConditionalFieldsGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<UpdateAttestationConditionalFieldsGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation/conditionalGroup/${body.formField.groupID}`,
      body.formField,
      { timeout: SAVE_REQUEST_TIMEOUT_MS },
    ));
  }

  async addAttestationGroup(body: AddAttestationGroupRequestDTO): Promise<Result<AttestationGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<AttestationGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation/group/${body.groupId}`,
    ));
  }

  async removeAttestationGroup(body: RemoveAttestationGroupRequestDTO): Promise<Result<AttestationGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.delete<AttestationGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation/group/${body.groupId}`,
    ));
  }

  async clearAttestationGroup(body: ClearAttestationGroupRequestDTO): Promise<Result<ClearAttestationGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<ClearAttestationGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/attestation/group/${body.groupId}/clear`,
    ));
  }

  async uploadFile(body: UploadFileToAttestationRequestDTO): Promise<Result<string, AppError>> {
    const formData = new FormData();
    formData.append('File', body.file);

    return wrapHttpCall(() => this.api.post<string>(
      `/ApplicationForm/${body.applicationId}/attestation/uploadFile/${body.itemId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: body.onUploadProgress,
        signal: body.cancelToken,
      },
    ));
  }

  async downloadFile(body: DownloadFileFromAttestationRequestDTO): Promise<Result<unknown, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() => this.api.get(
      `/ApplicationForm/${body.applicationId}/attestation/downloadFile/${body.itemId}${queryString}`,
    ));
  }

  async deleteFile(body: DeleteFileFromAttestationRequestDTO): Promise<Result<boolean, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() => this.api.post<boolean>(
      `/ApplicationForm/${body.applicationId}/attestation/deleteFile/${body.itemId}${queryString}`,
    ));
  }
}
